import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    BadgePercent,
    Pencil,
    Plus,
    Search,
    ShieldCheck,
    SlidersHorizontal,
    Trash2,
} from "lucide-react";
import {
    payrollDeleteResourceRequest,
    payrollFetchResourceRequest,
    payrollSaveResourceRequest,
} from "../../../redux/actions/payrollActions";
import "./ParamCotisationsPaie.css";

const emptyTaux = {
    code: "",
    libelle: "",
    tauxSalarial: "",
    tauxPatronal: "",
    plafondMensuel: "",
    dateDebut: new Date().toISOString().slice(0, 10),
    dateFin: "",
};

const COTISATION_EXAMPLES = [
    "CNSS_AMO",
    "CNSS_PRESTATIONS_SOCIALES",
    "CNSS_ALLOCATIONS_FAMILIALES",
    "CNSS_TAXE_FORMATION_PROF",
];

const toNumberOrNull = (value) =>
    value === "" || value === null || value === undefined ? null : Number(value);

const formatMoney = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return "—";
    return number.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatRate = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return "—";
    return `${number.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 4 })} %`;
};

const formatDate = (value) => {
    if (!value) return "—";
    return new Date(value).toLocaleDateString("fr-FR");
};

/* ─── Modal ─────────────────────────────────────────────────── */
const TauxModal = ({ open, editing, loading, onClose, onSubmit }) => {
    const [form, setForm] = useState(emptyTaux);

    useEffect(() => {
        setForm(
            editing
                ? {
                    code: editing.code || "",
                    libelle: editing.libelle || "",
                    tauxSalarial: editing.tauxSalarial ?? "",
                    tauxPatronal: editing.tauxPatronal ?? "",
                    plafondMensuel: editing.plafondMensuel ?? "",
                    dateDebut: editing.dateDebut || emptyTaux.dateDebut,
                    dateFin: editing.dateFin || "",
                }
                : emptyTaux
        );
    }, [editing, open]);

    if (!open) return null;

    const change = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const submit = (e) => {
        e.preventDefault();
        if (
            !form.code.trim() ||
            !form.libelle.trim() ||
            form.tauxSalarial === "" ||
            form.tauxPatronal === "" ||
            !form.dateDebut
        ) {
            Swal.fire({
                icon: "warning",
                text: "Code, nom, taux salarial, taux patronal et date de début sont obligatoires.",
            });
            return;
        }
        onSubmit({
            code: form.code.trim(),
            libelle: form.libelle.trim(),
            tauxSalarial: Number(form.tauxSalarial),
            tauxPatronal: Number(form.tauxPatronal),
            plafondMensuel: toNumberOrNull(form.plafondMensuel),
            dateDebut: form.dateDebut,
            dateFin: form.dateFin || null,
        });
    };

    return (
        <div className="pcp-overlay" onClick={onClose}>
            <div className="pcp-modal" onClick={(e) => e.stopPropagation()}>
                <div className="pcp-modal-header">
                    <h3>{editing ? "Modifier une cotisation" : "Ajouter une cotisation"}</h3>
                    <button className="pcp-modal-close" type="button" onClick={onClose}>✕</button>
                </div>
                <form className="pcp-form" onSubmit={submit}>
                    <div className="pcp-section-title">Identification</div>
                    <div className="pcp-grid">
                        <div className="pcp-field pcp-full">
                            <label>Code *</label>
                            <input
                                name="code"
                                value={form.code}
                                onChange={change}
                                placeholder="Ex : CNSS_AMO"
                                list="pcp-codes-cotisation"
                            />
                            <datalist id="pcp-codes-cotisation">
                                {COTISATION_EXAMPLES.map((c) => (
                                    <option key={c} value={c} />
                                ))}
                            </datalist>
                        </div>
                        <div className="pcp-field pcp-full">
                            <label>Nom bulletin</label>
                            <input
                                name="libelle"
                                value={form.libelle}
                                onChange={change}
                                placeholder="Ex : Retenue A.M.O."
                            />
                        </div>
                    </div>

                    <div className="pcp-section-title">Taux et plafond</div>
                    <div className="pcp-grid">
                        <div className="pcp-field">
                            <label>Taux salarial *</label>
                            <input
                                name="tauxSalarial"
                                type="number"
                                step="0.0001"
                                value={form.tauxSalarial}
                                onChange={change}
                                placeholder="Ex : 4.4800"
                            />
                        </div>
                        <div className="pcp-field">
                            <label>Taux patronal *</label>
                            <input
                                name="tauxPatronal"
                                type="number"
                                step="0.0001"
                                value={form.tauxPatronal}
                                onChange={change}
                                placeholder="Ex : 8.9800"
                            />
                        </div>
                        <div className="pcp-field pcp-full">
                            <label>Plafond mensuel</label>
                            <input
                                name="plafondMensuel"
                                type="number"
                                step="0.01"
                                value={form.plafondMensuel}
                                onChange={change}
                                placeholder="Vide = aucun plafond"
                            />
                        </div>
                    </div>

                    <div className="pcp-section-title">Validité historique</div>
                    <div className="pcp-grid">
                        <div className="pcp-field">
                            <label>Date de début *</label>
                            <input name="dateDebut" type="date" value={form.dateDebut} onChange={change} />
                        </div>
                        <div className="pcp-field">
                            <label>Date de fin</label>
                            <input name="dateFin" type="date" value={form.dateFin} onChange={change} />
                        </div>
                    </div>

                    <div className="pcp-modal-actions">
                        <button
                            type="button"
                            className="pcp-btn pcp-btn-outline"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            className="pcp-btn pcp-btn-primary"
                            disabled={loading}
                        >
                            {editing ? "Enregistrer" : "Créer"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

/* ─── Page principale ────────────────────────────────────────── */
const ParamCotisationsPaie = () => {
    const dispatch = useDispatch();
    const payroll = useSelector((state) => state.payroll || {});
    const tauxCotisations = payroll.tauxCotisations || [];
    const [searchTerm, setSearchTerm] = useState("");
    const [modal, setModal] = useState({ open: false, editing: null });

    useEffect(() => {
        dispatch(payrollFetchResourceRequest("tauxCotisations"));
    }, [dispatch]);

    const filtered = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return tauxCotisations;
        return tauxCotisations.filter((row) =>
            [row.code, row.libelle, row.nom, row.name, row.tauxSalarial, row.tauxPatronal, row.plafondMensuel].some(
                (v) => String(v ?? "").toLowerCase().includes(q)
            )
        );
    }, [tauxCotisations, searchTerm]);

    const closeModal = () => setModal({ open: false, editing: null });

    const saveCotisation = (payload) => {
        dispatch(
            payrollSaveResourceRequest("tauxCotisations", payload, modal.editing?.id || null)
        );
        closeModal();
    };

    const deleteItem = async (row) => {
        const result = await Swal.fire({
            title: "Supprimer",
            text: `Supprimer "${row.code}" ?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Oui, supprimer",
            cancelButtonText: "Annuler",
        });
        if (result.isConfirmed)
            dispatch(payrollDeleteResourceRequest("tauxCotisations", row.id));
    };

    return (
        <div className="pcp-page">
            <div className="pcp-content">
                {/* En-tête */}
                <div className="pcp-header">
                    <div>
                        <h2>
                            <ShieldCheck size={18} />
                            Gestion des cotisations
                        </h2>
                        <p className="pcp-subtitle">
                            Paramétrez les pourcentages salariaux, patronaux et plafonds utilisés dans le calcul de paie.
                        </p>
                    </div>
                    <div className="pcp-header-actions">
                        <button
                            className="pcp-btn pcp-btn-assign"
                            type="button"
                            onClick={() => dispatch(payrollFetchResourceRequest("tauxCotisations"))}
                        >
                            <SlidersHorizontal size={14} />
                            Actualiser
                        </button>
                        <button
                            className="pcp-btn pcp-btn-primary"
                            type="button"
                            onClick={() => setModal({ open: true, editing: null })}
                        >
                            <Plus size={14} />
                            Ajouter une cotisation
                        </button>
                    </div>
                </div>

                {/* Barre de recherche */}
                <div className="pcp-toolbar">
                    <div className="pcp-search-wrap">
                        <Search size={15} />
                        <input
                            className="pcp-search"
                            placeholder="Rechercher par code, taux ou plafond..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="pcp-meta">
                        {payroll.loading ? "Chargement..." : `${filtered.length} cotisation(s)`}
                    </div>
                </div>

                {payroll.error && (
                    <div className="pcp-alert pcp-alert-danger">{payroll.error}</div>
                )}

                {/* Tableau liste */}
                <div className="pcp-list-wrapper">
                    <table className="pcp-list-table">
                        <thead>
                        <tr>
                            <th>Code</th>
                            <th>Nom bulletin</th>
                            <th className="pcp-th-right">Taux salarial</th>
                            <th className="pcp-th-right">Taux patronal</th>
                            <th className="pcp-th-right">Plafond mensuel</th>
                            <th>Date début</th>
                            <th>Date fin</th>
                            <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        {filtered.length === 0 ? (
                            <tr>
                                <td colSpan={8} className="pcp-list-empty">
                                    Aucune cotisation trouvée
                                </td>
                            </tr>
                        ) : (
                            filtered.map((row) => (
                                <tr key={row.id}>
                                    <td>
                                        <span className="pcp-code">{row.code}</span>
                                    </td>
                                    <td className="pcp-list-libelle">
                                        {row.libelle || row.nom || row.name || "—"}
                                    </td>
                                    <td className="pcp-td-right pcp-td-rate">
                                    {formatRate(row.tauxSalarial)}
                                    </td>
                                    <td className="pcp-td-right pcp-td-rate">
                                        {formatRate(row.tauxPatronal)}
                                    </td>
                                    <td className="pcp-td-right">
                                        {row.plafondMensuel
                                            ? formatMoney(row.plafondMensuel)
                                            : <span className="pcp-none">Aucun</span>}
                                    </td>
                                    <td className="pcp-td-date">{formatDate(row.dateDebut)}</td>
                                    <td className="pcp-td-date">
                                        {row.dateFin
                                            ? formatDate(row.dateFin)
                                            : <span className="pcp-badge-actif">Actif</span>}
                                    </td>
                                    <td>
                                        <div className="pcp-row-actions">
                                            <button
                                                className="pcp-link-btn"
                                                type="button"
                                                title="Modifier"
                                                onClick={() => setModal({ open: true, editing: row })}
                                            >
                                                <Pencil size={13} />
                                            </button>
                                            <button
                                                className="pcp-link-btn danger"
                                                type="button"
                                                title="Supprimer"
                                                onClick={() => deleteItem(row)}
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>
            </div>

            <TauxModal
                open={modal.open}
                editing={modal.editing}
                loading={payroll.saving}
                onClose={closeModal}
                onSubmit={saveCotisation}
            />
        </div>
    );
};

export default ParamCotisationsPaie;